/* eslint-disable no-param-reassign */
import type { ElementType, MutableRefObject, ReactNode } from 'react';
import React, { useRef, useState } from 'react';

import type { TextProps as MantineTextProps } from '@mantine/core';
import { Text as MantineText } from '@mantine/core';

import type { TooltipProps } from '@/new_src/view/components/Tooltip';
import { Tooltip } from '@/new_src/view/components/Tooltip';

interface TextProps extends Omit<MantineTextProps, 'children'> {
  children: ReactNode;
  dataPrivate?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  component?: ElementType;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ children, truncate = true, dataPrivate = false, tooltipProps, lineClamp, ...props }, ref) => {
    const textRef = useRef<HTMLElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const handleMouseEnter = () => {
      const textEl = textRef.current;

      if (!textEl) {
        return;
      }

      const widthOverflow = textEl.scrollWidth > textEl.clientWidth;

      if (lineClamp > 1) {
        const heightOverflow = textEl.scrollHeight > textEl.clientHeight;
        setIsTruncated(widthOverflow || heightOverflow);
        return;
      }

      setIsTruncated(widthOverflow);
    };

    /**
     * Mantine의 polymorphic 컴포넌트 타입 호환성을 처리하기 위한 callback ref
     * MantineText는 'component' prop에 따라 다양한 HTML 요소(p, span, div 등)로 렌더링될 수 있음
     * callback ref를 사용하여 예상되는 ref 타입이 일치하지 않을 때 발생하는 TypeScript 오류를 회피
     */
    const setRef = (el: HTMLElement | null) => {
      textRef.current = el;
      if (ref) {
        (ref as MutableRefObject<HTMLElement | null>).current = el;
      }
    };

    return (
      <Tooltip label={children} disabled={!isTruncated} {...tooltipProps}>
        <MantineText
          ref={setRef}
          truncate={truncate}
          data-private={dataPrivate}
          onMouseEnter={handleMouseEnter}
          lineClamp={lineClamp}
          lts="-0.5%"
          style={{
            wordBreak: 'keep-all',
            ...props.style,
          }}
          {...(props as MantineTextProps)}
        >
          {children}
        </MantineText>
      </Tooltip>
    );
  }
);
